import { defineStore } from 'pinia';
import { getMarketCollectionById } from '@/api/market';
import { classifyFeedFetchError } from '@/utils/request-error.js';
import { scheduleFeedFairOddsRefresh } from '@/stores/feed-fair-odds-refresh.js';

/**
 * 收藏页 Feed 状态（按收藏 ID 拉 collection/get，与首页/探索页 store 隔离）
 */
export const useWatchlistFeedStore = defineStore('watchlistFeed', {
  state: () => ({
    initialized: false,
    rawRows: [],
    listLoading: false,
    loadGeneration: 0,
    /** @type {'network' | 'server' | null} */
    listFetchError: null,
  }),

  getters: {
    showListEmpty(state) {
      return !state.listLoading && state.rawRows.length === 0 && !state.listFetchError;
    },
  },

  actions: {
    recoverIfStuck() {
      if (!this.initialized && !this.listLoading) return;
      if (this.rawRows.length) return;
      if (!this.listLoading) {
        if (this.initialized) this.initialized = false;
        return;
      }
      this.listLoading = false;
      this.initialized = false;
    },

    /** 按收藏 ID 列表拉取 collection/get */
    async loadFromIds(ids) {
      this.recoverIfStuck();

      const idList = [...new Set(ids.map((id) => String(id ?? '').trim()).filter(Boolean))];
      if (!idList.length) {
        this.rawRows = [];
        this.listFetchError = null;
        this.initialized = true;
        return;
      }

      if (this.listLoading) return;

      const gen = ++this.loadGeneration;
      this.listLoading = true;
      this.listFetchError = null;

      try {
        const results = await Promise.allSettled(idList.map((id) => getMarketCollectionById(id)));
        if (gen !== this.loadGeneration) return;

        const rows = results
          .map((result) => (result.status === 'fulfilled' ? result.value : null))
          .filter(Boolean);

        if (!rows.length) {
          const rejected = results.find((result) => result.status === 'rejected');
          this.listFetchError = rejected ? classifyFeedFetchError(rejected.reason) : 'server';
        }

        this.rawRows = rows;
        this.initialized = true;
      } catch (err) {
        if (gen !== this.loadGeneration) return;
        console.warn('[watchlistFeed] loadFromIds failed', err);
        this.listFetchError = classifyFeedFetchError(err);
        this.rawRows = [];
        this.initialized = true;
      } finally {
        if (gen === this.loadGeneration) {
          this.listLoading = false;
        }
      }
      // FairOdds：列表已展示后再后台 batchPrice（不阻塞）
      if (gen === this.loadGeneration && this.rawRows.length) {
        scheduleFeedFairOddsRefresh(this);
      }
    },

    /** 取消收藏后从 rawRows 移除对应项 */
    pruneByIds(ids) {
      if (!this.rawRows.length) return;
      const idSet = new Set(ids.map((id) => String(id ?? '').trim()).filter(Boolean));
      this.rawRows = this.rawRows.filter((row) => idSet.has(String(row.id ?? '').trim()));
    },

    /** 收藏 ID 新增时增量拉 collection/get（不整页 reload） */
    async appendFromIds(ids) {
      const idList = [...new Set(ids.map((id) => String(id ?? '').trim()).filter(Boolean))];
      if (!idList.length) return;

      const existing = new Set(
        this.rawRows.map((row) => String(row.id ?? '').trim()).filter(Boolean)
      );
      const pending = idList.filter((id) => !existing.has(id));
      if (!pending.length) return;

      const gen = ++this.loadGeneration;

      try {
        const results = await Promise.allSettled(pending.map((id) => getMarketCollectionById(id)));
        if (gen !== this.loadGeneration) return;

        const newRows = results
          .map((result) => (result.status === 'fulfilled' ? result.value : null))
          .filter(Boolean);

        if (newRows.length) {
          this.rawRows = [...this.rawRows, ...newRows];
          scheduleFeedFairOddsRefresh(this);
        }
        this.initialized = true;
      } catch (err) {
        if (gen !== this.loadGeneration) return;
        console.warn('[watchlistFeed] appendFromIds failed', err);
      }
    },
  },
});
